import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { api } from '../utils/client';

interface ContactStrategistModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const ContactStrategistModal: React.FC<ContactStrategistModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [question, setQuestion] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setQuestion('');
    setIsSubmitting(false);
    setError(null);
  }, [open]);

  if (!open) return null;

  const trimmedQuestion = question.trim();
  const canSubmit = trimmedQuestion.length > 0 && !isSubmitting;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;

    setIsSubmitting(true);
    setError(null);
    try {
      await api('/api/strategist-contact-requests/', {
        method: 'POST',
        body: JSON.stringify({ question: trimmedQuestion }),
      });
      onSuccess();
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to send request';
      setError(message);
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[15400] bg-black/45 backdrop-blur-sm flex items-center justify-center p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="contact-strategist-title"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-6 pt-6 pb-4">
          <h2 id="contact-strategist-title" className="text-xl font-bold text-[#0C0B0B]">
            Contact a Catalyx Strategist
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-gray-900 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors shrink-0"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="px-6 pb-4">
            <label htmlFor="strategist-question" className="block text-sm text-[#626262] mb-2">
              What would you like to ask?
            </label>
            <textarea
              id="strategist-question"
              value={question}
              onChange={(event) => setQuestion(event.target.value)}
              rows={5}
              maxLength={5000}
              placeholder="Type your question here"
              className="w-full rounded-lg border border-[#E8E8E8] bg-white px-3 py-2 text-[14px] text-[#0C0B0B] placeholder:text-[#9A9A9A] resize-y focus:outline-none focus:ring-1 focus:ring-[#674fa3]"
            />
            {error && (
              <p className="mt-2 text-sm text-red-600">{error}</p>
            )}
          </div>
          <div className="px-6 pb-6 flex justify-end">
            <button
              type="submit"
              disabled={!canSubmit}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${
                canSubmit
                  ? 'bg-[#674fa3] text-white hover:bg-[#5a4490]'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isSubmitting ? 'Sending…' : 'Request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContactStrategistModal;
